import { forwardRef, type HTMLAttributes, type ReactElement, type ReactNode } from 'react'
import { Circle } from 'lucide-react'
import { cn } from '@/lib/utils'
import './badge.css'

/**
 * - `neutral`: plain state or a count, such as Planned or 4 cameras.
 * - `quiet`: inactive or low-emphasis state, such as Draft, Idle or Low.
 * - `accent`: work in progress, such as In progress.
 * - `selection`: the host's media or camera selection. Never an error.
 * - `live`: live designation backed by real media evidence. Always shows a glyph.
 * - `success`, `warning`, `error`: outcomes and recovery states.
 * - `overlay`: a plain label floating over media or a map (white on the overlay shadow), such as what a picture-in-picture
 *   card holds. Says what is there, never a state.
 */
export type BadgeTone = 'neutral' | 'quiet' | 'accent' | 'selection' | 'live' | 'success' | 'warning' | 'error' | 'overlay'

/** `compact` sits inside a line of text without growing it: no block padding, so it is the xs line's height. */
export type BadgeSize = 'default' | 'compact'

export type BadgeProps = Omit<HTMLAttributes<HTMLSpanElement>, 'children'> & {
  tone?: BadgeTone
  size?: BadgeSize
  /** A Lucide icon before the text. Decorative: the text says the state. Replaces the live dot on `live`. */
  icon?: ReactElement
  /**
   * `live`'s dot only (ignored with an `icon`): the dot pulses gently where motion is welcome (still under reduced
   * motion), for a public page saying that something is live now. Leave it off in the live workspace, which stays quiet.
   */
  pulse?: boolean
  /** Short text: one or two words, or a count. */
  children: ReactNode
}

/** A short status label: a pill whose text carries the meaning and whose tone supports it. Not interactive. */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { tone = 'neutral', size = 'default', icon, pulse = false, className, children, ...props },
  ref,
) {
  const glyph = icon ?? (tone === 'live' ? <Circle fill="currentColor" /> : null)
  return (
    <span
      ref={ref}
      className={cn('play-badge', `play-badge--${tone}`, size === 'compact' && 'play-badge--compact', pulse && tone === 'live' && !icon && 'play-badge--pulse', className)}
      {...props}
    >
      {glyph && <span className="play-badge__icon" aria-hidden="true">{glyph}</span>}
      <span className="play-badge__label">{children}</span>
    </span>
  )
})
